import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import Cookies from "js-cookie";
import httpClient from "../../../../api/httpClient";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal, Edit, Trash2 } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { fallbackName } from "../Utils/util";
import { useDispatch, useSelector } from "react-redux";
import { setListViewState, addTab } from "../../../../redux/slices/invoiceSlice";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import { useInvoiceListData } from "./invoiceShared";
import { buildFilterNode, countActiveFilters } from "../../../AdvancedFilter/filterTree";
import { AdvancedFilterToolbar } from "../../../AdvancedFilter/AdvancedFilterToolbar";
import { AdvancedFilterModal } from "../../../AdvancedFilter/AdvancedFilterModal";
import { SavedFiltersControl } from "../../../AdvancedFilter/SavedFiltersControl";
import { INVOICE_RULE_FIELDS } from "../invoicesFilterFields";

const SORTABLE_COLUMNS = {
  "Invoice Number":        "invoiceNumber",
  "Invoice Date":          "invoiceDate",
  "Invoice Total Charges": "total",
  "Invoice Status":        "invoiceStatus",
  "Pay To":                "payToCompanyName",
  "Bill To":               "billToCompanyName",
  "Office Code":           "officeCode",
};


const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Invoice Number",
  "Invoice Date",
  "Invoice Total Charges",
  "Invoice Status",
  "Pay To",
  "Bill To",
  "Office Code"
];


export default function DraftsInvoicesTab({
  searchQuery = "",
  onUpdateCount = () => {},
  onInvoiceClick = () => {},
  stateKey = "drafts-invoices",
  refreshKey = 0,
  isSearchTab = false,
}) {
  const dispatch = useDispatch();
  const listViewState = useSelector((s) => s.invoices.listViewState || {});
  const savedView = listViewState[stateKey] || {};
  const [page, setPage] = useState(savedView.currentPage || 1);
  const [size, setSize] = useState(savedView.itemsPerPage || 50);
  const [sortBy, setSortBy] = useState(savedView.sortBy ?? "invoiceDate");
  const [ascending, setAscending] = useState(savedView.ascending ?? false);
  const [selected, setSelected] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [columnWidths, setColumnWidths] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({ rules: [], matchType: "AND" });
  const [showFilterModal, setShowFilterModal] = useState(false);

  const tableViews = useTableSavedViews({
    pageKey: stateKey || TABLE_PAGE_KEYS.INVOICE_DRAFTS,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Invoice Number"],
    getExtraConfig: () => ({ currentPage: page, pageSize: size }),
    onApplyConfig: (config) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setSize(config.pageSize);
    },
  });

  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;
  
  const API = process.env.REACT_APP_API_BASE_URL + "/api";

  useEffect(() => {
    dispatch(
      setListViewState({
        stateKey,
        currentPage: page,
        itemsPerPage: size,
        sortBy,
        ascending,
      })
    );
  }, [dispatch, stateKey, page, size, sortBy, ascending]);

  const handleSort = (newSortBy) => {
    setPage(1);
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  // Close menus on outside clicks
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('[data-action-trigger]')) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
      if (
        bulkMenuRef.current &&
        !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current &&
        !checkboxHeaderRef.current.contains(e.target)
      ) {
        setShowBulkMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenuId(prev => (prev === id ? null : id));
  };

  // Memoized: an unmemoized recompute creates a new object reference every render, which
  // retriggers useInvoiceListData's fetch effect in an infinite loop (see MyinvoiceTab.js).
  const filterNode = useMemo(
    () => buildFilterNode(INVOICE_RULE_FIELDS, advancedFilters),
    [advancedFilters]
  );

  const {
    invoices: drafts,
    totalElements,
    totalPages,
    showLoadingOverlay,
    errorMessage,
    refetch: fetchDrafts,
  } = useInvoiceListData({
    invoiceStatus: "Draft",
    searchQuery,
    page,
    size,
    refreshKey,
    sortBy,
    ascending,
    onPageOutOfRange: setPage,
    filter: filterNode,
  });

  useEffect(() => {
    onUpdateCount(totalElements);
  }, [totalElements, onUpdateCount]);

  const handleSelectAll = (checked) => {
    setSelected(checked
      ? drafts.map(d => d.uniqueId)
      : []
    );
  };

  const toggleSelect = (id) => {
    setSelected(sel =>
      sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]
    );
  };

  const handleResume = (invoice) => {
        dispatch(
      addTab({
        name: `Edit Draft (${invoice.invoiceNumber})`,
        type: "new",
        data: invoice  
      })
    );
    setOpenMenuId(null);
  };

  const handleDeleteSingle = (invoice) => {
        setDeleteTarget({ type: 'single', data: invoice });
    setShowDeleteConfirm(true);
    setOpenMenuId(null);
  };

  const handleDeleteBulk = () => {
    if (selected.length === 0) {
      notify.warning({
        title: "No selection",
        message: "Please select at least one draft to delete.",
      });
      return;
    }
    
    const selectedInvoices = drafts.filter(d => selected.includes(d.uniqueId));
    setDeleteTarget({ type: 'bulk', data: selectedInvoices });
    setShowDeleteConfirm(true);
    setShowBulkMenu(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    
    try {
      const invoicesToDelete = deleteTarget.type === 'single' 
        ? [deleteTarget.data] 
        : deleteTarget.data;
      
      const deletePromises = invoicesToDelete.map(async (inv) => {
        await httpClient.delete(
          `${API}/invoices/${inv.uniqueId}`,
          {
            headers: {
              "Office-Code": inv.billToOfficeCode,
              Authorization: `Bearer ${Cookies.get("authToken")}`,
            },
          }
        );
        
        if (Array.isArray(inv.shipments)) {
          await Promise.all(
            inv.shipments.map(sh =>
              httpClient.put(
                `${API}/shipments/${sh.shipmentNumber}`,
                { isInvoiced: false },
                { 
                  headers: { 
                    Authorization: `Bearer ${Cookies.get("authToken")}`,
                    "Office-Code": inv.billToOfficeCode 
                  } 
                }
              )
            )
          );
        }
      });
      
      await Promise.all(deletePromises);
      
      const count = invoicesToDelete.length;
      notify.success({
        title: "Delete successful",
        message: `${count} draft invoice${count > 1 ? 's' : ''} deleted successfully.`,
      });
      
      setSelected([]);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      
      await fetchDrafts();
      
    } catch (err) {
      console.error("Delete failed:", err);
      notify.error({
        title: "Delete failed",
        message: err?.response?.data?.message || "Could not delete draft invoice(s).",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  // Create a memoized map of invoices for quick lookup
  const invoiceMap = useRef({});
  useEffect(() => {
    invoiceMap.current = {};
    drafts.forEach(inv => {
      invoiceMap.current[inv.uniqueId] = inv;
    });
  }, [drafts]);

  const rows = drafts.map(inv => ({
    Selection:        inv.uniqueId,
    Action:           inv,
    "Invoice Number": inv.invoiceNumber,
    "Invoice Date":   inv.invoiceDate,
    "Invoice Total Charges": `${inv.total?.toFixed(2)} ${inv.currency || ""}`,
    "Invoice Status": (inv.invoiceStatus || "-").toUpperCase(),
    "Pay To":         inv.payToCompanyName || "-",
    "Bill To":        fallbackName(inv.billToCompanyName) || "-",
    "Office Code":    inv.officeCode || "-",
    __actions: [
      {
        icon:  "Edit",
        label: "Resume",
        color: "blue",
        action: async () => {
                    const invoice = invoiceMap.current[inv.uniqueId] || inv;
          handleResume(invoice);
        }
      },
      {
        icon:  "Trash",
        label: "Delete",
        color: "red",
        action: async () => {
                    const invoice = invoiceMap.current[inv.uniqueId] || inv;
          handleDeleteSingle(invoice);
        }
      }
    ]
  }));

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {showLoadingOverlay ? (
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message="Loading drafts..."
        />
      ) : (
        <>
          {/* Error banner — surfaces fetch failures instead of silently showing 0 drafts */}
          {errorMessage && (
            <div className="shrink-0 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {errorMessage}
            </div>
          )}
          {!isSearchTab && (
            <AdvancedFilterToolbar
              ruleFields={INVOICE_RULE_FIELDS}
              filters={advancedFilters}
              activeCount={countActiveFilters(INVOICE_RULE_FIELDS, advancedFilters)}
              onOpenModal={() => setShowFilterModal(true)}
              onClearAll={() => setAdvancedFilters({ rules: [], matchType: "AND" })}
              onChangeFilters={setAdvancedFilters}
              trailing={
                <div className="flex items-center gap-2">
                  <SavedFiltersControl
                    pageKey={stateKey || TABLE_PAGE_KEYS.INVOICE_DRAFTS}
                    ruleFields={INVOICE_RULE_FIELDS}
                    currentFilters={advancedFilters}
                    onApply={setAdvancedFilters}
                  />
                  <TableSavedViewsControl
                    controlLabel={controlLabel}
                    views={views}
                    activeView={activeView}
                    loading={viewsLoading}
                    error={viewsError}
                    isDirty={isDirty}
                    allDataHeaders={TABLE_HEADERS.slice(2)}
                    dataHeaders={dataHeaders}
                    onSelectView={selectView}
                    onSaveNew={saveNewView}
                    onUpdateActive={updateActiveView}
                    onRename={renameView}
                    onDelete={deleteView}
                    onSetDefault={setDefaultView}
                    onUpdateColumnVisibility={updateColumnVisibility}
                    onManageColumns={onManageColumns}
                  />
                </div>
              }
            />
          )}

          {isSearchTab && (
            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={TABLE_HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          )}
{showFilterModal && (
            <AdvancedFilterModal
              ruleFields={INVOICE_RULE_FIELDS}
              filters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setShowFilterModal(false)}
            />
          )}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={rows}
              initialColumnWidths={columnWidths}
              onColumnResize={setColumnWidths}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => setShowBulkMenu(v => !v)}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
  const id     = row.Selection;
  const isOpen = openMenuId === id;
  const orderedDataHeaders = (visibleColumns ?? [])
    .map((col) => col.id)
    .filter((id) => id !== "Selection" && id !== "Action");
  const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

  return (
    <>
      {/* Column 0 — Checkbox */}
      <td
        className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
        style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
        onDoubleClick={() => handleResume(row.Action)}
      >
        <input
          type="checkbox"
          className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
          checked={selected.includes(id)}
          onChange={() => toggleSelect(id)}
        />
      </td>

      {/* Column 1 — Action (portal dropdown) */}
      <td
        onClick={(e) => e.stopPropagation()}
        className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
        style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
      >
        <button
          data-action-trigger
          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
          onClick={(e) => handleActionClick(id, e)}
        >
          <MoreHorizontal size={18} className="mx-auto" />
        </button>
      </td>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
        >
          <ActionMenu
            rowId={id}
            isOpen
            moduleName="Invoice"
            onClose={() => setOpenMenuId(null)}
            actions={row.__actions}
          />
        </div>,
        document.body
      )}

      {/* Columns 2+ — Data */}
      {dataHeadersToRender.map((header) => {
        return (
          <td
            key={header}
            className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(header).className}`}
            style={{
              ...getStickyCellProps(header).style,
              whiteSpace:   "nowrap",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              width:        "200px",
              minWidth:     "200px",
              maxWidth:     "200px",
            }}
            onDoubleClick={() => handleResume(row.Action)}
          >
            {row[header]}
          </td>
        );
      })}
    </>
  );
}}
            </Table>
            
            {showBulkMenu && checkboxHeaderRef.current && (
              <div
                ref={bulkMenuRef}
                className="absolute z-[1000]"
                style={{
                  top: checkboxHeaderRef.current.getBoundingClientRect().bottom + window.scrollY + 6,
                  left: checkboxHeaderRef.current.getBoundingClientRect().left + window.scrollX,
                }}
              >
                <ActionMenu
                  rowId="bulk"
                  isOpen
                  onClose={() => setShowBulkMenu(false)}
                  moduleName="Draft"
                  placement="header"
                  actions={[
                    {
                      icon: "Trash",
                      label: "Delete Selected",
                      color: "red",
                      action: handleDeleteBulk
                    }
                  ]}
                />
              </div>
            )}
          </div>

          <GlobalPagination
            label="Drafts per page"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={size}
            onItemsPerPageChange={setSize}
          />
        </>
      )}
      
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-acs-dark-text mb-2">
                  Delete Draft Invoice{deleteTarget.type === 'bulk' && deleteTarget.data.length > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-gray-600 dark:text-acs-dark-subtext mb-4">
                  {deleteTarget.type === 'single' 
                    ? `Are you sure you want to delete draft invoice ${deleteTarget.data.invoiceNumber}? This action cannot be undone.`
                    : `Are you sure you want to delete ${deleteTarget.data.length} draft invoices? This action cannot be undone.`
                  }
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 rounded text-sm bg-gray-400 text-white hover:bg-gray-500 transition-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 rounded text-sm bg-red-600 text-white hover:bg-red-700 transition-none flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        <span>Delete</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}